import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type CacheState, type CustomerDetail } from '../../api/admin';
import { ApiError } from '../../api/client';
import { AdminLayout } from '../../components/AdminLayout';

export function AdminCustomerEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const customerQuery = useQuery({
    queryKey: ['admin', 'customers', id],
    queryFn: () => adminApi.getCustomer(id!),
    enabled: !!id,
  });

  if (!id) return null;

  if (customerQuery.isLoading) {
    return (
      <AdminLayout>
        <p className="text-sm text-wc-muted">Loading…</p>
      </AdminLayout>
    );
  }
  if (customerQuery.isError || !customerQuery.data) {
    return (
      <AdminLayout>
        <p className="text-sm text-wc-red">Failed to load customer.</p>
      </AdminLayout>
    );
  }

  const customer = customerQuery.data;

  const handleDelete = async () => {
    if (!confirm(`Delete "${customer.displayName}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteCustomer(customer.id);
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
      navigate('/admin');
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Failed to delete customer.');
    }
  };

  const refreshCustomer = (next: CustomerDetail) => {
    qc.setQueryData(['admin', 'customers', customer.id], next);
    qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
  };

  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/admin" className="text-xs uppercase tracking-wider text-wc-muted-strong hover:text-wc-blue">
            ← Back to customers
          </Link>
          <h1 className="mt-2 font-serif text-2xl text-wc-black">{customer.displayName}</h1>
          <p className="text-sm text-wc-muted-strong">
            <span className="font-mono">{customer.slug}</span>
            {' · '}created {new Date(customer.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/admin/customers/${customer.id}/audit`}
            className="inline-flex items-center gap-1.5 rounded-md border border-wc-blue px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-wc-blue transition-colors hover:bg-wc-blue hover:text-wc-white"
            title="View this customer's audit dashboard"
          >
            View audit dashboard
          </Link>
          <button
            type="button"
            onClick={handleDelete}
            className="text-xs font-semibold uppercase tracking-wider text-wc-red hover:underline"
          >
            Delete customer
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-8">
        <GeneralSection customer={customer} onUpdated={refreshCustomer} />
        <EmailDomainsSection customer={customer} onUpdated={refreshCustomer} />
        <OidcSection customer={customer} onUpdated={refreshCustomer} />
        <EmailAuthSection customer={customer} onUpdated={refreshCustomer} />
        <GitlabSection customer={customer} onUpdated={refreshCustomer} />
        <ContentCacheSection customer={customer} />
        <PortalUsersSection customer={customer} onUpdated={refreshCustomer} />
      </div>
    </AdminLayout>
  );
}

function PortalUsersSection({ customer, onUpdated }: SectionProps) {
  const qc = useQueryClient();
  const usersQuery = useQuery({
    queryKey: ['admin', 'portal-users', customer.id],
    queryFn: () => adminApi.listPortalUsers(customer.id),
  });

  const mutation = useMutation({
    mutationFn: ({
      userId,
      patch,
    }: {
      userId: string;
      patch: Partial<{ canViewAuditDashboard: boolean; canReviewDocuments: boolean }>;
    }) => adminApi.updatePortalUser(customer.id, userId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'portal-users', customer.id] });
    },
  });

  // Customer-wide "everyone is a reviewer" toggle. Owned by Customer, so
  // saving routes through `updateCustomer` and re-seeds the cached customer
  // detail via `onUpdated` so the dimming state below picks up immediately.
  const allReviewMutation = useMutation({
    mutationFn: (next: boolean) =>
      adminApi.updateCustomer(customer.id, { allPortalUsersCanReview: next }),
    onSuccess: (updated) => {
      onUpdated(updated);
    },
  });
  // Customer-wide "global customer" toggle. When enabled, every user of
  // this customer can switch their portal session into any other active
  // customer (per-customer admin grants still gate audit/reviewer rights
  // on the visited tenant).
  const isGlobalMutation = useMutation({
    mutationFn: (next: boolean) =>
      adminApi.updateCustomer(customer.id, { isGlobal: next }),
    onSuccess: (updated) => {
      onUpdated(updated);
    },
  });

  const allReview = customer.allPortalUsersCanReview;
  const isGlobal = customer.isGlobal;

  return (
    <SectionShell
      title="Portal users & roles"
      description="Portal users are auto-created on first sign-in. Toggle access to grant a user the in-portal audit dashboard or the ability to move documents through the review workflow (draft / in review / approved)."
    >
      <div className="mb-4 flex flex-wrap gap-x-6 gap-y-3">
        <label
          className="inline-flex items-center gap-2 text-sm"
          title="When enabled, every portal user (including future sign-ups) can review documents regardless of their individual Reviewer checkbox below."
        >
          <input
            type="checkbox"
            checked={allReview}
            disabled={allReviewMutation.isPending}
            onChange={(e) => allReviewMutation.mutate(e.target.checked)}
          />
          <span className={allReview ? 'font-medium text-wc-blue' : 'text-wc-black'}>
            All portal users can review documents
          </span>
        </label>
        <label
          className="inline-flex items-center gap-2 text-sm"
          title="When enabled, users of this customer can pick any other active customer after sign-in and operate inside it. Per-customer admin grants still gate dashboard and reviewer rights on the visited tenant."
        >
          <input
            type="checkbox"
            checked={isGlobal}
            disabled={isGlobalMutation.isPending}
            onChange={(e) => isGlobalMutation.mutate(e.target.checked)}
          />
          <span className={isGlobal ? 'font-medium text-wc-blue' : 'text-wc-black'}>
            Global customer (users can act on any other customer)
          </span>
        </label>
      </div>
      {usersQuery.isLoading ? (
        <p className="text-sm text-wc-muted">Loading…</p>
      ) : usersQuery.isError ? (
        <p className="text-sm text-wc-red">Failed to load portal users.</p>
      ) : !usersQuery.data || usersQuery.data.length === 0 ? (
        <p className="text-sm text-wc-muted">
          No portal users yet. Users appear here after their first successful sign-in.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-wc-muted/15 text-sm">
            <thead className="bg-wc-gray/30 text-left text-xs uppercase tracking-wider text-wc-muted-strong">
              <tr>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Last sign-in</th>
                <th className="px-3 py-2 font-medium">Dashboard access</th>
                <th className="px-3 py-2 font-medium">Reviewer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-wc-muted/10">
              {usersQuery.data.map((user) => {
                const isImpersonating = user.homeCustomer.id !== customer.id;
                return (
                <tr key={user.id}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-wc-black">
                        {user.displayName ?? user.email}
                      </span>
                      {isImpersonating && (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800"
                          title={`Global user from "${user.homeCustomer.slug}". Permissions toggled here only apply when they have switched into ${customer.slug}.`}
                        >
                          via {user.homeCustomer.slug}
                        </span>
                      )}
                    </div>
                    {user.displayName && (
                      <div className="text-xs text-wc-muted-strong">{user.email}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-wc-muted-strong">
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleString()
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <label className="inline-flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={user.canViewAuditDashboard}
                        disabled={mutation.isPending}
                        onChange={(e) =>
                          mutation.mutate({
                            userId: user.id,
                            patch: { canViewAuditDashboard: e.target.checked },
                          })
                        }
                      />
                      <span
                        className={
                          user.canViewAuditDashboard
                            ? 'font-medium text-wc-blue'
                            : 'text-wc-muted-strong'
                        }
                      >
                        {user.canViewAuditDashboard ? 'Granted' : 'Not granted'}
                      </span>
                    </label>
                  </td>
                  <td className="px-3 py-2">
                    <label
                      className={`inline-flex items-center gap-2 text-xs ${
                        allReview ? 'opacity-60' : ''
                      }`}
                      title={
                        allReview
                          ? "Overridden by 'All portal users can review documents'"
                          : undefined
                      }
                    >
                      <input
                        type="checkbox"
                        checked={user.canReviewDocuments || allReview}
                        disabled={mutation.isPending || allReview}
                        onChange={(e) =>
                          mutation.mutate({
                            userId: user.id,
                            patch: { canReviewDocuments: e.target.checked },
                          })
                        }
                      />
                      <span
                        className={
                          user.canReviewDocuments || allReview
                            ? 'font-medium text-wc-blue'
                            : 'text-wc-muted-strong'
                        }
                      >
                        {allReview
                          ? 'Granted (everyone)'
                          : user.canReviewDocuments
                            ? 'Granted'
                            : 'Not granted'}
                      </span>
                    </label>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionShell>
  );
}

interface SectionProps {
  customer: CustomerDetail;
  onUpdated: (next: CustomerDetail) => void;
}

function SectionShell({
  title,
  description,
  badge,
  children,
}: {
  title: string;
  description?: string;
  badge?: { label: string; tone: 'active' | 'muted' };
  children: React.ReactNode;
}) {
  return (
    <section className="card p-8">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <h2 className="font-serif text-xl text-wc-black">{title}</h2>
          {badge && (
            <span
              className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                badge.tone === 'active'
                  ? 'bg-wc-blue/10 text-wc-blue'
                  : 'bg-wc-muted/15 text-wc-muted-strong'
              }`}
            >
              {badge.label}
            </span>
          )}
        </div>
        {description && <p className="mt-1 text-sm text-wc-muted-strong">{description}</p>}
      </header>
      {children}
    </section>
  );
}

function GeneralSection({ customer, onUpdated }: SectionProps) {
  const [slug, setSlug] = useState(customer.slug);
  const [displayName, setDisplayName] = useState(customer.displayName);
  const [status, setStatus] = useState<'active' | 'suspended'>(customer.status === 'suspended' ? 'suspended' : 'active');
  const [authMethod, setAuthMethod] = useState<'oidc' | 'email_code'>(customer.authMethod);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setSlug(customer.slug);
    setDisplayName(customer.displayName);
    setStatus(customer.status === 'suspended' ? 'suspended' : 'active');
    setAuthMethod(customer.authMethod);
  }, [customer.slug, customer.displayName, customer.status, customer.authMethod]);

  const mutation = useMutation({
    mutationFn: () =>
      adminApi.updateCustomer(customer.id, {
        slug: slug.trim().toLowerCase(),
        displayName: displayName.trim(),
        status,
        authMethod,
      }),
    onSuccess: (next) => {
      onUpdated(next);
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  return (
    <SectionShell
      title="General"
      description="Basic identification + which authentication method this customer's users sign in with."
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="grid gap-5 sm:grid-cols-2"
      >
        <div>
          <label className="field-label">Display name</label>
          <input
            className="field-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={120}
            required
          />
        </div>
        <div>
          <label className="field-label">Slug</label>
          <input
            className="field-input font-mono"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            pattern="^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$"
            maxLength={64}
            required
          />
        </div>
        <div>
          <label className="field-label">Status</label>
          <select
            className="field-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as 'active' | 'suspended')}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <div>
          <label className="field-label">Authentication method</label>
          <select
            className="field-input"
            value={authMethod}
            onChange={(e) => setAuthMethod(e.target.value as 'oidc' | 'email_code')}
          >
            <option value="oidc">OIDC (Microsoft Entra ID)</option>
            <option value="email_code">Email one-time code</option>
          </select>
          <p className="mt-1 text-xs text-wc-muted">
            {authMethod === 'oidc'
              ? "Users are redirected to the customer's Entra ID tenant. Make sure the OIDC section below is filled in before saving."
              : "Users receive a 6-digit code via the customer's own SMTP server. Make sure the Email auth section below is filled in before saving."}
          </p>
        </div>
        <div className="sm:col-span-2 flex items-center gap-4">
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
          {savedAt && <SavedFlag at={savedAt} />}
          {error && <span className="text-sm text-wc-red">{error}</span>}
        </div>
      </form>
    </SectionShell>
  );
}

function EmailAuthSection({ customer, onUpdated }: SectionProps) {
  const cfg = customer.email;
  const [provider, setProvider] = useState<'smtp' | 'graph'>(cfg?.provider ?? 'smtp');
  const [smtpHost, setSmtpHost] = useState(cfg?.smtpHost ?? '');
  const [smtpPort, setSmtpPort] = useState(cfg?.smtpPort?.toString() ?? '587');
  const [smtpSecure, setSmtpSecure] = useState(cfg?.smtpSecure ?? false);
  const [smtpUser, setSmtpUser] = useState(cfg?.smtpUser ?? '');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [graphTenantId, setGraphTenantId] = useState(cfg?.graphTenantId ?? '');
  const [graphClientId, setGraphClientId] = useState(cfg?.graphClientId ?? '');
  const [graphClientSecret, setGraphClientSecret] = useState('');
  const [fromAddress, setFromAddress] = useState(cfg?.fromAddress ?? '');
  const [fromName, setFromName] = useState(cfg?.fromName ?? '');
  const [subjectTemplate, setSubjectTemplate] = useState(cfg?.subjectTemplate ?? '');
  const [headingTemplate, setHeadingTemplate] = useState(cfg?.headingTemplate ?? '');
  const [bodyFormat, setBodyFormat] = useState<'html' | 'text'>(cfg?.bodyFormat ?? 'html');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setProvider(customer.email?.provider ?? 'smtp');
    setSmtpHost(customer.email?.smtpHost ?? '');
    setSmtpPort(customer.email?.smtpPort?.toString() ?? '587');
    setSmtpSecure(customer.email?.smtpSecure ?? false);
    setSmtpUser(customer.email?.smtpUser ?? '');
    setGraphTenantId(customer.email?.graphTenantId ?? '');
    setGraphClientId(customer.email?.graphClientId ?? '');
    setFromAddress(customer.email?.fromAddress ?? '');
    setFromName(customer.email?.fromName ?? '');
    setSubjectTemplate(customer.email?.subjectTemplate ?? '');
    setHeadingTemplate(customer.email?.headingTemplate ?? '');
    setBodyFormat(customer.email?.bodyFormat ?? 'html');
    setSmtpPassword('');
    setGraphClientSecret('');
  }, [customer.email]);

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.setEmailAuth(customer.id, {
        provider,
        fromAddress: fromAddress.trim(),
        fromName: fromName.trim() || undefined,
        subjectTemplate: subjectTemplate.trim() || undefined,
        headingTemplate: headingTemplate.trim() || undefined,
        bodyFormat,
        ...(provider === 'smtp'
          ? {
              smtpHost: smtpHost.trim() || undefined,
              smtpPort: smtpPort ? Number.parseInt(smtpPort, 10) : undefined,
              smtpSecure,
              smtpUser: smtpUser.trim() || undefined,
              smtpPassword: smtpPassword || undefined,
            }
          : {
              graphTenantId: graphTenantId.trim() || undefined,
              graphClientId: graphClientId.trim() || undefined,
              graphClientSecret: graphClientSecret || undefined,
            }),
      }),
    onSuccess: (next) => {
      onUpdated(next);
      setError(null);
      setSavedAt(Date.now());
      setSmtpPassword('');
      setGraphClientSecret('');
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  const clearMutation = useMutation({
    mutationFn: () => adminApi.clearEmailAuth(customer.id),
    onSuccess: onUpdated,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to remove.'),
  });

  return (
    <SectionShell
      title="Email provider"
      description="Used to send 6-digit sign-in codes when this customer is on the email-code method. Codes are 6 digits, expire in 10 minutes, single-use, and capped at 5 wrong attempts."
      badge={{
        label: customer.authMethod === 'email_code' ? 'In use' : 'Inactive',
        tone: customer.authMethod === 'email_code' ? 'active' : 'muted',
      }}
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          saveMutation.mutate();
        }}
        className="grid gap-5 sm:grid-cols-2"
      >
        <div className="sm:col-span-2">
          <label className="field-label">Provider</label>
          <div className="mt-1 inline-flex rounded-md border border-wc-muted/30 bg-wc-gray/40 p-1">
            <ProviderTab
              checked={provider === 'smtp'}
              onClick={() => setProvider('smtp')}
              label="SMTP"
            />
            <ProviderTab
              checked={provider === 'graph'}
              onClick={() => setProvider('graph')}
              label="Microsoft Graph"
            />
          </div>
          <p className="mt-2 text-xs text-wc-muted">
            {provider === 'smtp'
              ? 'Submits via a standard SMTP relay using nodemailer.'
              : 'Submits via the Microsoft Graph API (client-credentials flow). Requires an Entra app with the Mail.Send application permission and admin consent.'}
          </p>
        </div>

        <div>
          <label className="field-label">From address</label>
          <input
            className="field-input"
            type="email"
            placeholder={provider === 'graph' ? 'portal@acme.com (mailbox UPN)' : 'portal@acme.com'}
            value={fromAddress}
            onChange={(e) => setFromAddress(e.target.value)}
            required
          />
          {provider === 'graph' && (
            <p className="mt-1 text-xs text-wc-muted">
              Must be a real mailbox UPN in the same tenant — Graph sends from this exact account.
            </p>
          )}
        </div>
        <div>
          <label className="field-label">From name (optional)</label>
          <input
            className="field-input"
            placeholder="Acme Portal"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Email format</label>
          <select
            className="field-input"
            value={bodyFormat}
            onChange={(e) => setBodyFormat(e.target.value as 'html' | 'text')}
          >
            <option value="html">HTML (default)</option>
            <option value="text">Plain text only</option>
          </select>
          <p className="mt-1 text-xs text-wc-muted">
            Plain text avoids HTML-stripping spam filters on some enterprise mail
            gateways. The 6-digit code is included identically in both formats.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Subject template (optional)</label>
          <input
            className="field-input"
            placeholder="{customerName}: your sign-in code"
            value={subjectTemplate}
            onChange={(e) => setSubjectTemplate(e.target.value)}
          />
          <p className="mt-1 text-xs text-wc-muted">
            <code className="font-mono">{'{customerName}'}</code> is substituted in. Leave
            blank to use the default subject.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Email heading (optional)</label>
          <input
            className="field-input"
            placeholder="Welcome to {customerName}."
            value={headingTemplate}
            onChange={(e) => setHeadingTemplate(e.target.value)}
          />
          <p className="mt-1 text-xs text-wc-muted">
            Big serif headline at the top of the email body.{' '}
            <code className="font-mono">{'{customerName}'}</code> is substituted in. Leave
            blank for the default <em>“Welcome to {'{customerName}'}.”</em>.
          </p>
        </div>

        {provider === 'smtp' ? (
          <>
            <div>
              <label className="field-label">SMTP host</label>
              <input
                className="field-input"
                placeholder="smtp.example.com"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">Port</label>
                <input
                  className="field-input"
                  type="number"
                  min={1}
                  max={65535}
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-wc-black">
                  <input
                    type="checkbox"
                    checked={smtpSecure}
                    onChange={(e) => setSmtpSecure(e.target.checked)}
                  />
                  TLS on connect
                </label>
              </div>
            </div>
            <div>
              <label className="field-label">SMTP user (optional)</label>
              <input
                className="field-input"
                value={smtpUser}
                onChange={(e) => setSmtpUser(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="field-label">
                SMTP password{' '}
                {customer.email?.smtpPasswordSet && (
                  <span className="ml-2 inline-flex rounded-full bg-wc-blue/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-wc-blue">
                    already set
                  </span>
                )}
              </label>
              <input
                className="field-input font-mono"
                type="password"
                placeholder={
                  customer.email?.smtpPasswordSet ? '(leave blank to keep current)' : 'paste secret'
                }
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </>
        ) : (
          <>
            <div className="sm:col-span-2 rounded-md bg-wc-gray/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-wc-muted-strong">
                Entra app registration
              </p>
              <p className="mt-1 text-xs text-wc-muted">
                Register a dedicated app in <strong>Entra ID → App registrations</strong>, grant the{' '}
                <code className="font-mono">Mail.Send</code> <strong>application</strong> permission on{' '}
                <code className="font-mono">Microsoft Graph</code>, click <strong>Grant admin consent</strong>,
                then paste the tenant + client IDs and a client secret below. This must be a separate
                app from the OIDC sign-in registration.
              </p>
            </div>
            <div>
              <label className="field-label">Tenant ID</label>
              <input
                className="field-input font-mono"
                placeholder="00000000-0000-0000-0000-000000000000"
                value={graphTenantId}
                onChange={(e) => setGraphTenantId(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="field-label">Client ID</label>
              <input
                className="field-input font-mono"
                placeholder="00000000-0000-0000-0000-000000000000"
                value={graphClientId}
                onChange={(e) => setGraphClientId(e.target.value)}
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">
                Client secret{' '}
                {customer.email?.graphClientSecretSet && (
                  <span className="ml-2 inline-flex rounded-full bg-wc-blue/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-wc-blue">
                    already set
                  </span>
                )}
              </label>
              <input
                className="field-input font-mono"
                type="password"
                placeholder={
                  customer.email?.graphClientSecretSet
                    ? '(leave blank to keep current)'
                    : 'paste the secret value (not the secret ID)'
                }
                value={graphClientSecret}
                onChange={(e) => setGraphClientSecret(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </>
        )}

        <div className="sm:col-span-2 flex items-center gap-4">
          <button type="submit" className="btn-primary" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save email config'}
          </button>
          {customer.email && (
            <button
              type="button"
              className="text-xs font-semibold uppercase tracking-wider text-wc-red hover:underline"
              onClick={() => {
                if (confirm('Remove the email auth configuration?')) clearMutation.mutate();
              }}
            >
              Remove
            </button>
          )}
          {savedAt && <SavedFlag at={savedAt} />}
          {error && <span className="text-sm text-wc-red">{error}</span>}
        </div>
      </form>
    </SectionShell>
  );
}

function ProviderTab({
  checked,
  onClick,
  label,
}: {
  checked: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
        checked ? 'bg-white text-wc-blue shadow-sm' : 'text-wc-muted-strong hover:text-wc-blue'
      }`}
    >
      {label}
    </button>
  );
}

function EmailDomainsSection({ customer, onUpdated }: SectionProps) {
  const [domains, setDomains] = useState<string[]>(customer.emailDomains);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => setDomains(customer.emailDomains), [customer.emailDomains]);

  const mutation = useMutation({
    mutationFn: () => adminApi.replaceDomains(customer.id, domains),
    onSuccess: (next) => {
      onUpdated(next);
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  const addDomain = () => {
    const value = draft.trim().toLowerCase();
    if (!value) return;
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(value)) {
      setError(`"${value}" doesn't look like a domain.`);
      return;
    }
    if (domains.includes(value)) {
      setError(`"${value}" is already in the list.`);
      return;
    }
    setError(null);
    setDomains([...domains, value]);
    setDraft('');
  };

  return (
    <SectionShell
      title="Email domains"
      description="Users whose email address ends with any of these domains are routed to this customer's identity provider."
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <input
          className="field-input"
          placeholder="acme.com"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addDomain();
            }
          }}
        />
        <button type="button" className="btn-secondary" onClick={addDomain}>
          Add
        </button>
      </div>

      <ul className="mt-4 flex flex-wrap gap-2">
        {domains.length === 0 && (
          <li className="text-sm text-wc-muted">No domains yet.</li>
        )}
        {domains.map((d) => (
          <li
            key={d}
            className="inline-flex items-center gap-2 rounded-full bg-wc-gray px-3 py-1.5 text-sm text-wc-black"
          >
            <span className="font-mono">{d}</span>
            <button
              type="button"
              className="text-wc-red hover:underline"
              onClick={() => setDomains(domains.filter((x) => x !== d))}
              aria-label={`Remove ${d}`}
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex items-center gap-4">
        <button
          type="button"
          className="btn-primary"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save domains'}
        </button>
        {savedAt && <SavedFlag at={savedAt} />}
        {error && <span className="text-sm text-wc-red">{error}</span>}
      </div>
    </SectionShell>
  );
}

function OidcSection({ customer, onUpdated }: SectionProps) {
  const [issuerUri, setIssuerUri] = useState(customer.oidc?.issuerUri ?? '');
  const [clientId, setClientId] = useState(customer.oidc?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [buttonLabel, setButtonLabel] = useState(customer.oidc?.buttonLabel ?? '');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setIssuerUri(customer.oidc?.issuerUri ?? '');
    setClientId(customer.oidc?.clientId ?? '');
    setButtonLabel(customer.oidc?.buttonLabel ?? '');
    setClientSecret('');
  }, [customer.oidc]);

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.setOidc(customer.id, {
        issuerUri: issuerUri.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret || undefined,
        buttonLabel: buttonLabel.trim() || undefined,
      }),
    onSuccess: (next) => {
      onUpdated(next);
      setError(null);
      setSavedAt(Date.now());
      setClientSecret('');
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  const clearMutation = useMutation({
    mutationFn: () => adminApi.clearOidc(customer.id),
    onSuccess: onUpdated,
  });

  return (
    <SectionShell
      title="OIDC (Microsoft Entra ID)"
      description={`Redirect URI to register in Entra: /auth/callback/${customer.slug}`}
      badge={{
        label: customer.authMethod === 'oidc' ? 'In use' : 'Inactive',
        tone: customer.authMethod === 'oidc' ? 'active' : 'muted',
      }}
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          saveMutation.mutate();
        }}
        className="grid gap-5 sm:grid-cols-2"
      >
        <div className="sm:col-span-2">
          <label className="field-label">Issuer URI</label>
          <input
            className="field-input"
            placeholder="https://login.microsoftonline.com/<tenant-id>/v2.0"
            value={issuerUri}
            onChange={(e) => setIssuerUri(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="field-label">Client ID</label>
          <input
            className="field-input font-mono"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="field-label">
            Client secret{' '}
            {customer.oidc?.clientSecretSet && (
              <span className="ml-2 inline-flex rounded-full bg-wc-blue/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-wc-blue">
                already set
              </span>
            )}
          </label>
          <input
            className="field-input font-mono"
            type="password"
            placeholder={customer.oidc?.clientSecretSet ? '(leave blank to keep current)' : 'paste secret here'}
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Button label (optional)</label>
          <input
            className="field-input"
            placeholder="Sign in with Acme"
            value={buttonLabel}
            onChange={(e) => setButtonLabel(e.target.value)}
          />
        </div>

        <div className="sm:col-span-2 flex items-center gap-4">
          <button type="submit" className="btn-primary" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save OIDC config'}
          </button>
          {customer.oidc && (
            <button
              type="button"
              className="text-xs font-semibold uppercase tracking-wider text-wc-red hover:underline"
              onClick={() => {
                if (confirm('Remove OIDC configuration?')) clearMutation.mutate();
              }}
            >
              Remove
            </button>
          )}
          {savedAt && <SavedFlag at={savedAt} />}
          {error && <span className="text-sm text-wc-red">{error}</span>}
        </div>
      </form>
    </SectionShell>
  );
}

function GitlabSection({ customer, onUpdated }: SectionProps) {
  const [mode, setMode] = useState<'repo' | 'pages'>(customer.gitlab?.mode ?? 'repo');
  const [baseUrl, setBaseUrl] = useState(customer.gitlab?.baseUrl ?? 'https://gitlab.com');
  const [projectId, setProjectId] = useState(customer.gitlab?.projectId ?? '');
  const [defaultBranch, setDefaultBranch] = useState(customer.gitlab?.defaultBranch ?? 'main');
  const [pagesUrl, setPagesUrl] = useState(customer.gitlab?.pagesUrl ?? '');
  const [pagesJobName, setPagesJobName] = useState(customer.gitlab?.pagesJobName ?? 'pages');
  const [accessToken, setAccessToken] = useState('');
  const [contentRoot, setContentRoot] = useState(customer.gitlab?.contentRoot ?? '');
  const [includePatterns, setIncludePatterns] = useState<string[]>(
    customer.gitlab?.includePatterns ?? [],
  );
  const [excludePatterns, setExcludePatterns] = useState<string[]>(
    customer.gitlab?.excludePatterns ?? [],
  );
  const [pdfAllowedPatterns, setPdfAllowedPatterns] = useState<string[]>(
    customer.gitlab?.pdfAllowedPatterns ?? [],
  );
  const [pdfBlockedPatterns, setPdfBlockedPatterns] = useState<string[]>(
    customer.gitlab?.pdfBlockedPatterns ?? [],
  );
  const [reviewExcludedPatterns, setReviewExcludedPatterns] = useState<string[]>(
    customer.gitlab?.reviewExcludedPatterns ?? [],
  );
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setMode(customer.gitlab?.mode ?? 'repo');
    setBaseUrl(customer.gitlab?.baseUrl ?? 'https://gitlab.com');
    setProjectId(customer.gitlab?.projectId ?? '');
    setDefaultBranch(customer.gitlab?.defaultBranch ?? 'main');
    setPagesUrl(customer.gitlab?.pagesUrl ?? '');
    setPagesJobName(customer.gitlab?.pagesJobName ?? 'pages');
    setContentRoot(customer.gitlab?.contentRoot ?? '');
    setIncludePatterns(customer.gitlab?.includePatterns ?? []);
    setExcludePatterns(customer.gitlab?.excludePatterns ?? []);
    setPdfAllowedPatterns(customer.gitlab?.pdfAllowedPatterns ?? []);
    setPdfBlockedPatterns(customer.gitlab?.pdfBlockedPatterns ?? []);
    setReviewExcludedPatterns(customer.gitlab?.reviewExcludedPatterns ?? []);
    setAccessToken('');
  }, [customer.gitlab]);

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.setGitlab(customer.id, {
        mode,
        baseUrl: baseUrl.trim() || undefined,
        projectId: projectId.trim() || undefined,
        defaultBranch: defaultBranch.trim() || undefined,
        pagesUrl: mode === 'pages' ? pagesUrl.trim() || undefined : undefined,
        pagesJobName: mode === 'pages' ? pagesJobName.trim() || undefined : undefined,
        accessToken: accessToken || undefined,
        contentRoot,
        includePatterns,
        excludePatterns,
        pdfAllowedPatterns,
        pdfBlockedPatterns,
        reviewExcludedPatterns,
      }),
    onSuccess: (next) => {
      onUpdated(next);
      setError(null);
      setSavedAt(Date.now());
      setAccessToken('');
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  const clearMutation = useMutation({
    mutationFn: () => adminApi.clearGitlab(customer.id),
    onSuccess: onUpdated,
  });

  return (
    <SectionShell
      title="GitLab content source"
      description="Choose between a raw GitLab repository (server-rendered tree) and an already-published GitLab Pages site (proxied through the portal)."
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          saveMutation.mutate();
        }}
        className="grid gap-5 sm:grid-cols-2"
      >
        <fieldset className="sm:col-span-2 grid gap-3 sm:grid-cols-2">
          <ModeRadio
            checked={mode === 'repo'}
            onChange={() => setMode('repo')}
            title="GitLab repository"
            description="Fetch files from the repo via the GitLab REST API. Markdown rendered and HTML sanitised server-side."
          />
          <ModeRadio
            checked={mode === 'pages'}
            onChange={() => setMode('pages')}
            title="GitLab Pages"
            description="Embed an already-built static site (mkdocs/hugo/jekyll/etc.). Proxied through the portal so access stays gated by the session."
          />
        </fieldset>

        <div className="sm:col-span-2">
          <label className="field-label">Base URL (GitLab instance)</label>
          <input
            className="field-input"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
        </div>

        {mode === 'repo' ? (
          <>
            <div>
              <label className="field-label">Project ID</label>
              <input
                className="field-input font-mono"
                placeholder="12345678 or group/project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="field-label">Default branch</label>
              <input
                className="field-input font-mono"
                value={defaultBranch}
                onChange={(e) => setDefaultBranch(e.target.value)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="sm:col-span-2">
              <label className="field-label">Pages URL</label>
              <input
                className="field-input"
                placeholder="https://<group>.gitlab-pages.example.com/<project>/"
                value={pagesUrl}
                onChange={(e) => setPagesUrl(e.target.value)}
              />
              <p className="mt-1 text-xs text-wc-muted">
                Required for <strong>public</strong> Pages — the portal will fetch the site directly through here. For private Pages, leave optional (used only to rewrite absolute URLs in the rendered HTML).
              </p>
            </div>
            <div className="sm:col-span-2 rounded-md bg-wc-gray/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-wc-muted-strong">
                Private Pages (via GitLab artifact API)
              </p>
              <p className="mt-1 text-xs text-wc-muted">
                GitLab Pages access control only honours browser-OAuth cookies, so a server-side token can&apos;t reach the Pages serving layer. Instead, fill in <strong>Project ID</strong> and an <strong>access token</strong> below — the portal will fetch the same files from the latest successful <code className="font-mono">pages</code> job&apos;s artifacts (which respect the token).
              </p>
            </div>
            <div>
              <label className="field-label">Project ID (for private Pages)</label>
              <input
                className="field-input font-mono"
                placeholder="12345678 or group/project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              />
            </div>
            <div>
              <label className="field-label">Default branch</label>
              <input
                className="field-input font-mono"
                value={defaultBranch}
                onChange={(e) => setDefaultBranch(e.target.value)}
              />
            </div>
            <div>
              <label className="field-label">Pages job name</label>
              <input
                className="field-input font-mono"
                placeholder="pages"
                value={pagesJobName}
                onChange={(e) => setPagesJobName(e.target.value)}
              />
              <p className="mt-1 text-xs text-wc-muted">
                Job in <code className="font-mono">.gitlab-ci.yml</code> that produces the Pages artifact.
              </p>
            </div>
          </>
        )}

        <div className="sm:col-span-2">
          <label className="field-label">Content root (optional)</label>
          <input
            className="field-input font-mono"
            placeholder={mode === 'repo' ? 'docs' : 'public'}
            value={contentRoot}
            onChange={(e) => setContentRoot(e.target.value)}
          />
          <p className="mt-1 text-xs text-wc-muted">
            {mode === 'repo' ? (
              <>Sub-folder inside the repo that becomes the portal root. Leave blank for the repo root.</>
            ) : projectId ? (
              <>Path inside the artifact archive that holds the published files. Defaults to <code className="font-mono">public</code> — that's where the conventional <code className="font-mono">pages</code> job uploads its output.</>
            ) : (
              <>Sub-path inside the Pages site (rarely needed). Leave blank to use the site root.</>
            )}
          </p>
        </div>

        <div className="sm:col-span-2 grid gap-5 rounded-md border border-wc-muted/20 p-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <h4 className="text-sm font-semibold text-wc-black">File-visibility filters</h4>
            <p className="mt-1 text-xs text-wc-muted">
              Glob patterns evaluated against each file&apos;s path <strong>relative to the content root</strong>.{' '}
              Use <code className="font-mono">**</code> to match across directories, e.g.{' '}
              <code className="font-mono">**/*.md</code>, <code className="font-mono">docs/**</code>,{' '}
              <code className="font-mono">**/*.draft.md</code>. Whitelist and blacklist take effect on the next cache refresh.
            </p>
          </div>
          <PatternListEditor
            label="Whitelist (include)"
            placeholder="**/*.md"
            patterns={includePatterns}
            onChange={setIncludePatterns}
            emptyHint="Empty = include every file from the repository."
            chipClassName="bg-wc-blue/10 text-wc-blue"
          />
          <PatternListEditor
            label="Blacklist (exclude)"
            placeholder="drafts/**"
            patterns={excludePatterns}
            onChange={setExcludePatterns}
            emptyHint="Empty = nothing is hidden."
            chipClassName="bg-wc-red/10 text-wc-red"
          />
        </div>

        <div className="sm:col-span-2 grid gap-5 rounded-md border border-wc-muted/20 p-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <h4 className="text-sm font-semibold text-wc-black">PDF download patterns</h4>
            <p className="mt-1 text-xs text-wc-muted">
              Opt-in glob patterns deciding which HTML pages get a <strong>Download as PDF</strong>{' '}
              button overlaid on the portal viewer. Patterns are evaluated against each file&apos;s path{' '}
              <strong>relative to the content root</strong>, just like the visibility filters above. Use{' '}
              <code className="font-mono">**/*.html</code> to enable PDFs for every HTML file, or restrict
              to subsections like <code className="font-mono">docs/**/*.html</code>. The blocklist always
              wins over the allowlist, so you can broadly enable and then carve out exceptions like{' '}
              <code className="font-mono">docs/internal/**</code>. Changes take effect immediately,
              without a cache refresh.
            </p>
          </div>
          <PatternListEditor
            label="Allow PDF download for"
            placeholder="docs/**/*.html"
            patterns={pdfAllowedPatterns}
            onChange={setPdfAllowedPatterns}
            emptyHint="Empty = no files can be downloaded as PDF."
            chipClassName="bg-wc-blue/10 text-wc-blue"
          />
          <PatternListEditor
            label="Block PDF download for"
            placeholder="docs/internal/**"
            patterns={pdfBlockedPatterns}
            onChange={setPdfBlockedPatterns}
            emptyHint="Empty = nothing is blocked."
            chipClassName="bg-wc-red/10 text-wc-red"
          />
        </div>

        <div className="sm:col-span-2">
          <div>
            <h4 className="text-sm font-semibold text-wc-black">Review workflow exclusions</h4>
            <p className="mt-1 text-xs text-wc-muted">
              Glob patterns marking files as <strong>excluded from the document review
              workflow</strong>. Excluded files render no review status badge in the portal
              tree, no <em>Reviewers</em> popover, and no status controls in the viewer toolbar.
              Patterns are evaluated against each file&apos;s path{' '}
              <strong>relative to the content root</strong>. Empty = every file is reviewable.
              Changes take effect immediately, without a cache refresh.
            </p>
          </div>
          <PatternListEditor
            label="Exclude from review"
            placeholder="legal/**"
            patterns={reviewExcludedPatterns}
            onChange={setReviewExcludedPatterns}
            emptyHint="Empty = every file is reviewable."
            chipClassName="bg-wc-muted/15 text-wc-muted-strong"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="field-label">
            {mode === 'repo' ? 'Personal access token' : 'Access token (optional)'}{' '}
            {customer.gitlab?.accessTokenSet && (
              <span className="ml-2 inline-flex rounded-full bg-wc-blue/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-wc-blue">
                already set
              </span>
            )}
          </label>
          <input
            className="field-input font-mono"
            type="password"
            placeholder={
              customer.gitlab?.accessTokenSet
                ? '(leave blank to keep current)'
                : mode === 'repo'
                  ? 'glpat-…'
                  : 'glpat-… (only if Pages site is private)'
            }
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            autoComplete="new-password"
          />
          <p className="mt-1 text-xs text-wc-muted">
            {mode === 'repo' ? (
              <>Needs the <code className="font-mono">read_repository</code> scope on the project above.</>
            ) : projectId ? (
              <>Required for private Pages. Needs the <code className="font-mono">read_api</code> scope on the project; the token must belong to an account with at least Reporter access.</>
            ) : (
              <>Not needed for public Pages fetched via URL.</>
            )}
          </p>
        </div>

        <div className="sm:col-span-2 flex items-center gap-4">
          <button type="submit" className="btn-primary" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save GitLab config'}
          </button>
          {customer.gitlab && (
            <button
              type="button"
              className="text-xs font-semibold uppercase tracking-wider text-wc-red hover:underline"
              onClick={() => {
                if (confirm('Remove GitLab configuration?')) clearMutation.mutate();
              }}
            >
              Remove
            </button>
          )}
          {savedAt && <SavedFlag at={savedAt} />}
          {error && <span className="text-sm text-wc-red">{error}</span>}
        </div>
      </form>
    </SectionShell>
  );
}

interface PatternListEditorProps {
  label: string;
  placeholder: string;
  patterns: string[];
  onChange: (next: string[]) => void;
  emptyHint: string;
  chipClassName: string;
}

const MAX_PATTERN_LENGTH = 256;

function PatternListEditor({
  label,
  placeholder,
  patterns,
  onChange,
  emptyHint,
  chipClassName,
}: PatternListEditorProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const value = draft.trim().replace(/^\/+/, '');
    if (!value) return;
    if (value.length > MAX_PATTERN_LENGTH) {
      setError(`Patterns must be at most ${MAX_PATTERN_LENGTH} characters.`);
      return;
    }
    if (patterns.includes(value)) {
      setError(`"${value}" is already in the list.`);
      return;
    }
    setError(null);
    onChange([...patterns, value]);
    setDraft('');
  };

  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <input
          className="field-input font-mono"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="btn-secondary" onClick={add}>
          Add
        </button>
      </div>
      <ul className="mt-2 flex flex-wrap gap-2">
        {patterns.length === 0 ? (
          <li className="text-xs text-wc-muted">{emptyHint}</li>
        ) : (
          patterns.map((p) => (
            <li
              key={p}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${chipClassName}`}
            >
              <span className="font-mono">{p}</span>
              <button
                type="button"
                className="hover:underline"
                onClick={() => onChange(patterns.filter((x) => x !== p))}
                aria-label={`Remove ${p}`}
              >
                ×
              </button>
            </li>
          ))
        )}
      </ul>
      {error && <p className="mt-1 text-xs text-wc-red">{error}</p>}
    </div>
  );
}

function ModeRadio({
  checked,
  onChange,
  title,
  description,
}: {
  checked: boolean;
  onChange: () => void;
  title: string;
  description: string;
}) {
  return (
    <label
      className={`flex cursor-pointer flex-col gap-1 rounded-md border p-4 text-sm transition-colors ${
        checked
          ? 'border-wc-blue bg-wc-blue/5'
          : 'border-wc-muted/30 hover:border-wc-blue/40'
      }`}
    >
      <span className="flex items-center gap-2">
        <input
          type="radio"
          name="gitlab-mode"
          checked={checked}
          onChange={onChange}
          className="accent-wc-blue"
        />
        <span className="font-semibold text-wc-black">{title}</span>
      </span>
      <span className="ml-6 text-xs text-wc-muted-strong">{description}</span>
    </label>
  );
}

function SavedFlag({ at }: { at: number }) {
  const fresh = Date.now() - at < 4000;
  if (!fresh) return null;
  return <span className="text-xs text-wc-muted">Saved.</span>;
}

function ContentCacheSection({ customer }: { customer: CustomerDetail }) {
  const qc = useQueryClient();
  const [scheduleMinutes, setScheduleMinutes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const statusQuery = useQuery({
    queryKey: ['admin', 'cache', customer.id],
    queryFn: () => adminApi.cacheStatus(customer.id),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === 'fetching' ? 2000 : false;
    },
  });

  useEffect(() => {
    if (statusQuery.data?.scheduleIntervalSec) {
      setScheduleMinutes(String(Math.round(statusQuery.data.scheduleIntervalSec / 60)));
    } else {
      setScheduleMinutes('');
    }
  }, [statusQuery.data?.scheduleIntervalSec]);

  const refreshMutation = useMutation({
    mutationFn: () => adminApi.refreshCache(customer.id),
    onSuccess: (next) => {
      qc.setQueryData(['admin', 'cache', customer.id], next);
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to start refresh.'),
  });

  const scheduleMutation = useMutation({
    mutationFn: (minutes: number | null) => adminApi.setCacheSchedule(customer.id, minutes),
    onSuccess: (next) => {
      qc.setQueryData(['admin', 'cache', customer.id], next);
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to save schedule.'),
  });

  const state: CacheState | undefined = statusQuery.data;
  const status = state?.status ?? 'empty';
  const lastRefreshedAt = state?.lastRefreshedAt ? new Date(state.lastRefreshedAt) : null;

  const onSaveSchedule = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = scheduleMinutes.trim();
    if (!trimmed) {
      scheduleMutation.mutate(null);
      return;
    }
    const minutes = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(minutes) || minutes < 1) {
      setError('Schedule must be a positive integer (minutes) or empty to disable.');
      return;
    }
    scheduleMutation.mutate(minutes);
  };

  return (
    <SectionShell
      title="Content cache"
      description="The server downloads the configured source once and serves it from local disk. Users never see GitLab during normal browsing."
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <p className="field-label">Status</p>
          <CacheStatusPill status={status} />
          {state?.fileCount !== null && state?.fileCount !== undefined && (
            <p className="mt-2 text-xs text-wc-muted">
              {state.fileCount} files · {formatBytes(state.sizeBytes ?? 0)}
            </p>
          )}
        </div>

        <div>
          <p className="field-label">Last refreshed</p>
          <p className="mt-1 text-sm text-wc-black">
            {lastRefreshedAt ? (
              <span title={lastRefreshedAt.toISOString()}>
                {formatRelative(lastRefreshedAt)}
              </span>
            ) : (
              <span className="text-wc-muted">Never</span>
            )}
          </p>
          {state?.lastRefreshRef && (
            <p className="mt-1 font-mono text-xs text-wc-muted">{state.lastRefreshRef.slice(0, 16)}</p>
          )}
        </div>

        {state?.lastRefreshError && (
          <div className="sm:col-span-2 rounded-md border border-wc-red/30 bg-wc-red/5 p-3 text-sm text-wc-red">
            <strong>Last error:</strong> {state.lastRefreshError}
          </div>
        )}

        <form onSubmit={onSaveSchedule} className="sm:col-span-2 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
          <div>
            <label className="field-label" htmlFor={`schedule-${customer.id}`}>
              Auto-refresh every (minutes)
            </label>
            <input
              id={`schedule-${customer.id}`}
              className="field-input"
              type="number"
              min={1}
              placeholder="leave empty to disable"
              value={scheduleMinutes}
              onChange={(e) => setScheduleMinutes(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-secondary self-end" disabled={scheduleMutation.isPending}>
            {scheduleMutation.isPending ? 'Saving…' : 'Save schedule'}
          </button>
          <button
            type="button"
            className="btn-primary self-end"
            onClick={() => refreshMutation.mutate()}
            disabled={status === 'fetching' || refreshMutation.isPending}
          >
            {status === 'fetching' ? 'Refreshing…' : 'Refresh now'}
          </button>
        </form>

        <div className="sm:col-span-2 flex items-center gap-3 text-sm">
          {state?.nextRefreshAt && status !== 'fetching' && (
            <span className="text-wc-muted">
              Next auto refresh: {new Date(state.nextRefreshAt).toLocaleString()}
            </span>
          )}
          {savedAt && <SavedFlag at={savedAt} />}
          {error && <span className="text-wc-red">{error}</span>}
        </div>
      </div>
    </SectionShell>
  );
}

function CacheStatusPill({ status }: { status: CacheState['status'] }) {
  const labels: Record<CacheState['status'], { text: string; classes: string }> = {
    empty: { text: 'Empty', classes: 'bg-wc-muted/15 text-wc-muted-strong' },
    fetching: { text: 'Refreshing…', classes: 'bg-wc-blue/10 text-wc-blue' },
    ok: { text: 'OK', classes: 'bg-wc-blue/10 text-wc-blue' },
    error: { text: 'Error', classes: 'bg-wc-red/10 text-wc-red' },
  };
  const { text, classes } = labels[status];
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${classes}`}>
      {text}
    </span>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatRelative(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
